import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Lišta „Nová verze" s tlačítkem Aktualizovat — desktop i PWA na mobilu.
 *
 * **Kontrola** se ptá serveru přímo (`/version.json`, no-store; porovnává
 * otisk sestavení, viz `zkontroluj`), ne přes
 * service worker: ten dostane zprávu jen tehdy, když stránku už ovládá, takže
 * po prvním načtení nebo tvrdém reloadu kontrola tiše neproběhla. Ptá se při
 * startu, každých 5 minut a hlavně **po návratu do aplikace** — PWA na
 * telefonu leží většinu času uspaná a interval v ní neběží.
 *
 * **Aktualizace** nesmí skončit tím, že se načte zase stará verze: smaže
 * cache service workeru, požádá ho o aktualizaci a načte stránku s parametrem
 * `?v=<verze>`, který obejde i HTTP cache. Parametr se po startu z adresy
 * zase odstraní. Assety má Vite s hashem, takže čerstvé HTML = čerstvá appka.
 *
 * **Umístění:** desktop pruh přes horní hranu, mobil plovoucí karta pod
 * stavovým řádkem (dřív ležel pruh na top: 0 pod výřezem a nešel stisknout)
 * — nad hlavičkou, modaly i odznakem TEST.
 */
export default function NovaVerze({ isMobile }) {
    const [nova, setNova] = useState(null);
    const [bezi, setBezi] = useState(false);

    // Porovnává se **otisk sestavení** (`build`), ne číslo verze: to se zvyšuje
    // jen při nasazení na produkci, takže na stagi lišta nikdy nepřišla.
    // Otisk se mění s každým nasazením. Číslo verze je jen popisek.
    const zkontroluj = useCallback(() => {
        const verze = document.querySelector('meta[name="app-version"]')?.content;
        const build = document.querySelector('meta[name="app-build"]')?.content;
        if (!verze && !build) return;
        fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => {
                if (!d) return;
                const jina = build && d.build ? d.build !== build : (d.current && d.current !== verze);
                if (jina) setNova({ verze: d.current !== verze ? d.current : null, build: d.build || d.current });
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        // Po aktualizaci uklidit `?v=` z adresy (hash s navigací zůstane)
        const u = new URL(window.location.href);
        if (u.searchParams.has('v')) {
            u.searchParams.delete('v');
            window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
        }

        zkontroluj();
        const i = setInterval(zkontroluj, 5 * 60_000);
        const naViditelnost = () => { if (document.visibilityState === 'visible') zkontroluj(); };
        window.addEventListener('app-resumed', zkontroluj);
        window.addEventListener('focus', zkontroluj);
        document.addEventListener('visibilitychange', naViditelnost);
        return () => {
            clearInterval(i);
            window.removeEventListener('app-resumed', zkontroluj);
            window.removeEventListener('focus', zkontroluj);
            document.removeEventListener('visibilitychange', naViditelnost);
        };
    }, [zkontroluj]);

    const aktualizovat = async () => {
        setBezi(true);
        try {
            const reg = await navigator.serviceWorker?.getRegistration();
            await reg?.update?.().catch(() => {});
            if (window.caches) {
                const klice = await caches.keys();
                await Promise.all(klice.map(k => caches.delete(k)));
            }
        } catch { /* aktualizace nesmí zůstat viset na úklidu */ }
        const u = new URL(window.location.href);
        u.searchParams.set('v', nova.build);
        window.location.replace(u.toString());
    };

    if (!nova) return null;

    const tlacitko = (
        <button type="button" onClick={aktualizovat} disabled={bezi}
            className="inline-flex items-center justify-center font-bold flex-shrink-0"
            style={{
                gap: 6, height: isMobile ? 40 : 30, padding: isMobile ? '0 16px' : '0 12px',
                borderRadius: isMobile ? 10 : 8, border: 'none', cursor: 'pointer',
                background: '#fff', color: '#B45309', fontSize: isMobile ? 14 : 13,
                WebkitTapHighlightColor: 'transparent',
            }}>
            <RefreshCw size={isMobile ? 16 : 14} className={bezi ? 'animate-spin' : undefined} />
            {bezi ? 'Aktualizuji…' : 'Aktualizovat'}
        </button>
    );

    return (
        <div role="status" style={isMobile ? {
            position: 'fixed', zIndex: 100001,
            top: 'calc(env(safe-area-inset-top, 0px) + 8px)', left: 12, right: 12,
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 10px 10px 14px', borderRadius: 14,
            background: 'linear-gradient(135deg, #FF8A00, #f59e0b)', color: '#fff',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        } : {
            position: 'fixed', zIndex: 100001, top: 0, left: 0, right: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
            padding: '6px 16px',
            background: 'linear-gradient(135deg, #FF8A00, #f59e0b)', color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
            <span style={{ flex: isMobile ? 1 : undefined, minWidth: 0, fontSize: isMobile ? 14 : 13, fontWeight: 600, lineHeight: 1.3 }}>
                {nova.verze ? `Nová verze ${nova.verze}` : 'Nová verze'}{isMobile ? <br /> : ' '}
                <span style={{ fontWeight: 500, opacity: 0.9 }}>je k dispozici</span>
            </span>
            {tlacitko}
        </div>
    );
}
